import { Dialog } from "@/components";
import { Button } from "@/components/ui";
import { RootState } from "@/redux/store";
import { useCreateOwnerMutation } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save } from "lucide-react";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "@/components/Toast";
import EditOwner from "../../Owners/OwnerDetails/EditOwner/EditOwner";
import { DEFAULT_COUNTRY, EDIT_OWNER_FORM_ID } from "../../Owners/OwnerDetails/EditOwner/useEditOwner";
import { createOwnerSchema } from "../../Owners/OwnerDetails/EditOwner/validations";

const ADDRESS_FIELDS = ["addressLine1", "addressLine2", "pobox", "city", "state", "zipCode", "country"];
const emptyAddress = () => Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, f === "country" ? DEFAULT_COUNTRY : ""]));
const blankToNull = (v: any) => (typeof v === "string" ? (v.trim() === "" ? null : v.trim()) : v ?? null);

interface NewOwnerDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  communityId: string;
  onCreated: (owner: any) => void;   // created user (userId, names, phone, email)
}

/** New Ownership: "Add new owner" when the search finds no match (same form as Owner Master > Add Owner). */
const NewOwnerDialog = ({ open, setOpen, communityId, onCreated }: NewOwnerDialogProps) => {
  const stateList = useSelector((state: RootState) => state.currentData.stateData) || [];
  const stateOptions = useMemo(() => stateList.map((s: any) => ({ label: s.name, value: s.code })), [stateList]);
  const toStateName = (v?: string) => stateList.find((s: any) => s.code == v)?.name || v || null;

  const form = useForm<any>({
    resolver: zodResolver(createOwnerSchema),
    defaultValues: {
      title: "", firstName: "", lastName: "", email: "", email2: "", phoneNumber: "", landlineNumber: "",
      address: emptyAddress(), separateCorrespondence: false, correspondenceAddress: emptyAddress(),
    },
  });

  const { mutate: createOwner, isPending } = useCreateOwnerMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string" ? error.response.data.message : "Something went wrong"
      );
    },
    onSuccess: (response: any) => {
      const owner = response?.data?.data;
      toast.success(`Owner ${owner?.userName || ""} created`);
      onCreated(owner);
      setOpen(false);
    },
  });

  const toAddress = (a: any) => ({
    ...Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, blankToNull(a?.[f])])),
    state: toStateName(a?.state),
  });

  const onSubmit = (d: any) => createOwner({
    data: {
      communityId: Number(communityId),
      title: blankToNull(d.title),
      firstName: blankToNull(d.firstName),
      lastName: blankToNull(d.lastName),
      email: blankToNull(d.email),
      phoneNumber: blankToNull(d.phoneNumber),
      landlineNumber: blankToNull(d.landlineNumber),
      address: toAddress(d.address),
      correspondenceAddress: d.separateCorrespondence ? toAddress(d.correspondenceAddress) : null,
    },
  });

  return (
    <Dialog open={open} setOpen={setOpen} hideFooter title="Add New Owner"
      contentClassName="lg:!max-w-[1100px] lg:!w-[1100px]" bodyClassName="max-h-[70vh]">
      <div className="flex flex-col gap-4">
        <EditOwner form={form} onSubmit={onSubmit} stateOptions={stateOptions} mode="create" />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isPending}>Cancel</Button>
          <Button type="submit" form={EDIT_OWNER_FORM_ID} disabled={isPending}><Save className="w-4 h-4" /> Save &amp; Add Owner</Button>
        </div>
      </div>
    </Dialog>
  );
};

export default NewOwnerDialog;
